/**
 * PDP delivery estimate — the countdown and the arrival date.
 *
 * A port of shipping-calculator/dates.py. Read that module before changing
 * anything here; the rules below are the ones its docstrings say are easiest
 * to get backwards, and a divergence between the two is invisible (both sides
 * keep rendering a plausible date).
 *
 *   * The 12:00 HKT cut-off, then roll forward to a working day. BOTH steps,
 *     IN THAT ORDER — rolling first lets a Friday 18:00 order dispatch on the
 *     Friday it has already missed.
 *
 *   * HK/MO get NO transit lookup at all: one calendar day, no working-day
 *     roll, because HK dispatch runs every calendar day including Sundays and
 *     public holidays. Only INTERNATIONAL processing is Mon-Fri.
 *
 *   * ⚠️ TWO DIFFERENT DEFINITIONS OF "BUSINESS DAY" LIVE HERE, DELIBERATELY.
 *     The dispatch roll and the processing day are OURS, so they skip weekends
 *     AND HK public holidays. A transit window carrying business_days_only
 *     skips WEEKENDS ONLY — dates._add applies no holiday calendar to it,
 *     because we have none for 22 destination markets and guessing one is
 *     worse than a window that is honest about counting weekdays. Collapsing
 *     these two into one helper is the tempting "cleanup" that silently makes
 *     every business-day estimate wrong.
 *
 *   * Peak slack widens the MAXIMUM only, and is keyed on the DISPATCH date
 *     rather than on now — an order placed 31 October that dispatches
 *     1 November travels in the peak.
 *
 * The window itself is NOT here: it arrives on data attributes, generated from
 * tables/transit.yaml. A destination with no measured lane never renders this
 * block at all, so there is no "unknown" branch to get wrong.
 */
(function () {
  'use strict';

  if (window.HillmalayaDeliveryEstimate) { return; }
  // An OBJECT, not `true`: still truthy for the double-include guard, and it
  // gives tools/check-delivery-estimate.js something to call. The date rules
  // are the part of this file that can be wrong without anything looking
  // wrong, so they are reachable by a check rather than only by a browser.
  var API = {};
  window.HillmalayaDeliveryEstimate = API;

  var CUTOFF_HOUR = 12;

  // Southeast Asia congests less than the long-haul lanes, so peak slack is
  // not flat. ⚠ East Asia is NOT Southeast Asia: CN, TW, MO, JP and KR take
  // the default. That is the split most likely to be "corrected" wrongly.
  var SEA = ['BN', 'ID', 'MY', 'PH', 'SG', 'TH', 'VN'];
  var PEAK_SLACK_SEA = 5;
  var PEAK_SLACK_DEFAULT = 10;

  /** A Date whose LOCAL getters read Hong Kong wall-clock values. HK has no
   *  DST, so a fixed +8 offset is exact rather than an approximation. */
  function hkNow() {
    var n = new Date();
    return new Date(n.getTime() + n.getTimezoneOffset() * 60000 + 8 * 3600000);
  }

  function midnight(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  function addDays(d, n) {
    var out = new Date(d.getTime());
    out.setDate(out.getDate() + n);
    return out;
  }

  function stamp(d) {
    var m = d.getMonth() + 1, day = d.getDate();
    return '' + d.getFullYear() + (m < 10 ? '0' : '') + m + (day < 10 ? '0' : '') + day;
  }

  function isWeekday(d) {
    var w = d.getDay();
    return w >= 1 && w <= 5;
  }

  /** OUR working day: weekday and not an HK public holiday. */
  function isWorkingHK(d, hol) {
    return isWeekday(d) && !hol[stamp(d)];
  }

  function rollToWorkingHK(d, hol) {
    var out = d, guard = 0;
    while (!isWorkingHK(out, hol) && guard++ < 30) { out = addDays(out, 1); }
    return out;
  }

  /** Advance `n` of OUR working days (weekends + HK holidays skipped). */
  function addWorkingHK(d, n, hol) {
    var out = d, guard = 0;
    while (n > 0 && guard++ < 400) {
      out = addDays(out, 1);
      if (isWorkingHK(out, hol)) { n--; }
    }
    return out;
  }

  /** Advance `n` WEEKDAYS. No holiday calendar — see the header note. */
  function addWeekdays(d, n) {
    var out = d, guard = 0;
    while (n > 0 && guard++ < 400) {
      out = addDays(out, 1);
      if (isWeekday(out)) { n--; }
    }
    return out;
  }

  function peakSlack(dispatch, cc) {
    var m = dispatch.getMonth() + 1;            // 1-12
    if (m !== 11 && m !== 12) { return 0; }
    return SEA.indexOf(cc) >= 0 ? PEAK_SLACK_SEA : PEAK_SLACK_DEFAULT;
  }

  /**
   * Placeholders are __TIME__ / __DATE__ / __FROM__ / __TO__, deliberately NOT
   * Shopify's own {{ name }} form: these strings come through the `| t` filter,
   * which runs its own interpolation over {{ }} and would consume the
   * placeholder before this file ever saw the template.
   */
  function interpolate(tpl, vars) {
    return tpl.replace(/__([A-Z]+)__/g, function (whole, key) {
      var k = key.toLowerCase();
      return Object.prototype.hasOwnProperty.call(vars, k) ? vars[k] : whole;
    });
  }

  function formatDate(d, locale) {
    try {
      return d.toLocaleDateString(locale, {
        weekday: 'short', day: 'numeric', month: 'short'
      });
    } catch (e) {
      // An unsupported locale tag must not cost the shopper the estimate.
      return d.toLocaleDateString();
    }
  }

  function compute(cfg, now) {
    var today = midnight(now);
    if (cfg.presaleState === 'blocked') { return null; }
    var incoming = cfg.presaleState === 'incoming';
    var warehouse;
    if (incoming) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(cfg.eta || '')) { return null; }
      var parts = cfg.eta.split('-').map(Number);
      warehouse = new Date(parts[0], parts[1] - 1, parts[2]);
      if (stamp(warehouse) !== cfg.eta.replace(/-/g, '') || warehouse < today) { return null; }
    }
    // The cut-off the shopper is racing. Past noon, it is tomorrow's — which
    // is also the day the parcel starts from, so one value answers both.
    var start = now.getHours() >= CUTOFF_HOUR ? addDays(today, 1) : today;
    if (incoming) { start = addDays(warehouse, 1); }
    var cutoff = new Date(start.getFullYear(), start.getMonth(), start.getDate(),
                          CUTOFF_HOUR, 0, 0, 0);

    var lo, hi;
    if (cfg.local) {
      // Hardcoded, not looked up: HK and MO carry no transit row by rule.
      lo = hi = addDays(start, 1);
    } else {
      var dispatch = incoming ? rollToWorkingHK(start, cfg.hol)
                              : addWorkingHK(rollToWorkingHK(start, cfg.hol), 1, cfg.hol);
      var slack = peakSlack(dispatch, cfg.cc);
      lo = cfg.biz ? addWeekdays(dispatch, cfg.min) : addDays(dispatch, cfg.min);
      hi = cfg.biz ? addWeekdays(dispatch, cfg.max + slack)
                   : addDays(dispatch, cfg.max + slack);
    }
    if (incoming && !cfg.local) {
      while (!isWeekday(lo)) { lo = addDays(lo, 1); }
      while (!isWeekday(hi)) { hi = addDays(hi, 1); }
    }
    return { cutoff: cutoff, lo: lo, hi: hi };
  }

  function countdownText(msLeft, cfg) {
    var mins = Math.max(0, Math.floor(msLeft / 60000));
    var h = Math.floor(mins / 60);
    var m = mins % 60;
    return h > 0 ? h + cfg.hLabel + ' ' + m + cfg.minLabel : m + cfg.minLabel;
  }

  function read(root) {
    var hol = {};
    (root.getAttribute('data-de-holidays') || '').split(',').forEach(function (s) {
      s = s.trim();
      if (s) { hol[s] = true; }
    });
    return {
      presaleState: root.getAttribute('data-de-presale-state') || '',
      eta: root.getAttribute('data-de-presale-eta') || '',
      presaleSentence: root.getAttribute('data-de-presale-sentence') || '',
      presaleRange: root.getAttribute('data-de-presale-range') || '',
      cc: root.getAttribute('data-de-cc') || '',
      local: root.getAttribute('data-de-local') === '1',
      min: parseInt(root.getAttribute('data-de-min'), 10) || 0,
      max: parseInt(root.getAttribute('data-de-max'), 10) || 0,
      biz: root.getAttribute('data-de-biz') === '1',
      hol: hol,
      sentence: root.getAttribute('data-de-sentence') || '',
      sentenceRange: root.getAttribute('data-de-sentence-range') || '',
      hLabel: root.getAttribute('data-de-h') || 'h',
      minLabel: root.getAttribute('data-de-min-unit') || 'min'
    };
  }

  function render(root, cfg, locale) {
    var now = hkNow();
    var r = compute(cfg, now);
    if (!r) { root.style.visibility = 'hidden'; return; }
    root.style.visibility = '';
    var incoming = cfg.presaleState === 'incoming';
    var time = countdownText(r.cutoff.getTime() - now.getTime(), cfg);
    var text;
    if (r.lo.getTime() === r.hi.getTime()) {
      text = interpolate(incoming ? cfg.presaleSentence : cfg.sentence, {
        time: time, date: formatDate(r.lo, locale)
      });
    } else {
      text = interpolate(incoming ? cfg.presaleRange : cfg.sentenceRange, {
        time: time,
        from: formatDate(r.lo, locale),
        to: formatDate(r.hi, locale)
      });
    }
    var slot = root.querySelector('[data-de-text]');
    // Only ever REPLACE text in a box that already has its height. The block
    // is never revealed by this script, so it cannot cause a layout shift.
    if (slot && text) { slot.textContent = text; }
  }

  function init() {
    var root = document.querySelector('[data-delivery-estimate]');
    if (!root) { return; }
    var cfg = read(root);
    if (!cfg.sentence || !cfg.sentenceRange) { return; }   // keep the fallback
    var locale = document.documentElement.getAttribute('lang') || undefined;

    API.refresh = function (target) { render(target, read(target), locale); };
    render(root, cfg, locale);
    // Once a minute is enough for an h/min readout, and it re-derives the
    // whole estimate rather than just ticking — so crossing noon rolls the
    // arrival date over without a reload.
    setInterval(function () { render(root, read(root), locale); }, 60000);
  }

  API.compute = compute;
  API.countdownText = countdownText;
  API.peakSlack = peakSlack;
  API.addWeekdays = addWeekdays;
  API.addWorkingHK = addWorkingHK;
  API.rollToWorkingHK = rollToWorkingHK;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());
